import { ResponseObject } from '../interfaces/App';
import User from '../interfaces/User';
import apiRequest from './apiService';

const getProviderUniqueId = (
  account: { homeAccountId?: string; tenantId?: string } | null
): string | null => {
  if (!account) return null;

  const { homeAccountId, tenantId } = account;
  if (!homeAccountId || !tenantId) return null;

  return homeAccountId.split(`-${tenantId.toLowerCase()}`)[0] || null;
};

const createUserIfNotExisting = async (
  account: any,
  inviteToken?: string
): Promise<[User | null, boolean]> => {
  const providerUniqueId = getProviderUniqueId(account);

  if (!providerUniqueId) {
    console.error('Problem while retrieving user');
    return [null, true];
  }

  try {
    const result = await selfRegister({
      user_id: providerUniqueId,
      email: account.username,
      name: account.name || '',
      language: navigator.language,
      ...(inviteToken !== undefined && { invite_token: inviteToken }),
    });

    if (!result?.message?.user) {
      console.error('self_register returned no user');
      return [null, true];
    }

    return [result.message.user as User, result.message.is_new as boolean];
  } catch (err: any) {
    console.error('Error in selfRegister:', err);
    return [null, true];
  }
};

const selfRegister = async (data: {
  user_id: string;
  email: string;
  name: string;
  language: string;
  invite_token?: string;
}): Promise<ResponseObject> => {
  try {
    const result = await apiRequest(
      'admin',
      'POST',
      '/user/self_register',
      data
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const createUser = async (data: any = {}): Promise<ResponseObject> => {
  try {
    const result = await apiRequest('admin', 'POST', '/user/create_user', data);
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const getUser = async (id: string): Promise<ResponseObject> => {
  try {
    const result: ResponseObject = await apiRequest(
      'admin',
      'GET',
      `/user/get_user/${id}`
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const getUserByEmail = async (email: string): Promise<ResponseObject> => {
  try {
    const result: ResponseObject = await apiRequest(
      'admin',
      'GET',
      `/user/get_user_by_email/${encodeURIComponent(email)}`
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const updateUser = async (id: string, data: any = {}) => {
  try {
    const result = await apiRequest(
      'admin',
      'PUT',
      `/user/update_user/${id}`,
      data
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

const deleteUser = async (id: string) => {
  try {
    const result = await apiRequest(
      'admin',
      'DELETE',
      `/user/delete_object/${id}`
    );
    return result;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export {
  createUserIfNotExisting,
  createUser,
  getUser,
  getUserByEmail,
  updateUser,
  deleteUser,
  getProviderUniqueId,
};
